import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { type OfferStatusCounts } from "../../../RepresentativeHome/useOfferStatusCounts";
import { type OfferTab } from "../../types";
import { tabStatuses } from "../../utils";

export const OFFER_TAB_LABEL_KEYS: Record<OfferTab, string> = {
  warning: "representativeOffers.tabs.needsAction",
  aggregatorAction: "representativeOffers.tabs.needsAggregatorAction",
  buyerAction: "representativeOffers.tabs.needsBuyerAction",
  healthy: "representativeOffers.tabs.inProgress",
  attention: "representativeOffers.tabs.closed",
  all: "representativeOffers.tabs.all",
};

export const REPRESENTATIVE_OFFER_TABS: OfferTab[] = ["warning", "healthy", "attention", "all"];

export const MANAGER_OFFER_TABS: OfferTab[] = [
  "aggregatorAction",
  "buyerAction",
  "healthy",
  "attention",
  "all",
];

interface OfferTabsProps {
  tabs: OfferTab[];
  value: OfferTab;
  onChange: (value: OfferTab) => void;
  counts: OfferStatusCounts;
  isLoading: boolean;
}

const sum = (values: (number | undefined)[]) =>
  values.reduce<number>((total, value) => total + (value ?? 0), 0);

const tabCount = (tab: OfferTab, counts: OfferStatusCounts) =>
  tab === "all" ? sum(Object.values(counts)) : sum(tabStatuses(tab).map(status => counts[status]));

export const OfferTabs = ({ tabs, value, onChange, counts, isLoading }: OfferTabsProps) => {
  const { t } = useTranslation();

  return (
    <Tabs value={value} onValueChange={next => onChange(next as OfferTab)}>
      <TabsList
        aria-label={t("representativeOffers.tabs.ariaLabel")}
        className="max-w-full gap-1 overflow-x-auto rounded-xl p-1 group-data-[orientation=horizontal]/tabs:h-auto"
      >
        {tabs.map(tab => (
          <TabsTrigger key={tab} value={tab} className="flex-none gap-2 rounded-lg px-4 py-2">
            {t(OFFER_TAB_LABEL_KEYS[tab])}
            {!isLoading && (
              <Badge variant={value === tab ? "default" : "secondary"} className="tabular-nums">
                {tabCount(tab, counts)}
              </Badge>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
